/**
 * Premium 360° turntable — all frames stacked (no src-swap flicker),
 * responsive drag + long inertia. Horizontal orbit only.
 */
class ZmSpin360 extends HTMLElement {
  constructor() {
    super();
    this.frames = [];
    this.imgs = [];
    this.count = 0;
    this.position = 0;
    this.velocity = 0;
    this.pointerId = null;
    this.lastX = 0;
    this.lastTs = 0;
    this.dragging = false;
    this.resumeAt = 0;
    this.shownIndex = -1;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.moveSamples = [];

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onKey = this.onKey.bind(this);
    this.tick = this.tick.bind(this);
  }

  connectedCallback() {
    this.media = this.querySelector('.zm-spin360__media');
    this.frames = this.parseFrames(this.dataset.frames);
    if (!this.frames.length) {
      this.frames = this.parseFrames(this.dataset.fallbackFrames);
    }

    /* Default ~22s — slow enough to read, fast enough to feel alive */
    this.cycleMs = Math.max((parseFloat(this.dataset.cycleSec) || 22) * 1000, 16000);
    this.auto =
      this.dataset.auto !== 'false' && !this.classList.contains('zm-hero-spin--paused');

    this.init();
  }

  parseFrames(raw) {
    if (!raw) return [];
    try {
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      const out = [];
      const seen = new Set();
      list.forEach((frame) => {
        if (!frame || !frame.url) return;
        const key = `${frame.url}|${frame.flip ? 1 : 0}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ url: frame.url, flip: !!frame.flip });
      });
      return out;
    } catch {
      return [];
    }
  }

  disconnectedCallback() {
    this.release();
    this.removeEventListener('wheel', this.onWheel);
    this.removeEventListener('keydown', this.onKey);
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  async init() {
    this.classList.add('is-loading');

    if (!this.frames.length) {
      await this.discoverMoreFrames();
    }

    this.frames = await this.preloadFrames(this.frames);
    this.count = Math.max(this.frames.length, 1);
    this.buildStack();

    this.classList.remove('is-loading');
    this.classList.add('is-ready');
    this.lastTs = performance.now();
    this.raf = requestAnimationFrame(this.tick);
    this.addEventListener('pointerdown', this.onDown);
    this.addEventListener('wheel', this.onWheel, { passive: false });
    this.addEventListener('keydown', this.onKey);
  }

  buildStack() {
    if (!this.media || !this.frames.length) return;

    /* Keep existing imgs if already correct count; else rebuild */
    this.media.querySelectorAll('.zm-spin360__img').forEach((el) => el.remove());
    this.imgs = this.frames.map((frame, i) => {
      const img = document.createElement('img');
      img.className = 'zm-spin360__img';
      img.alt = '';
      img.width = 1024;
      img.height = 1024;
      img.draggable = false;
      img.decoding = 'async';
      img.src = frame.url;
      if (frame.flip) img.style.transform = 'scaleX(-1)';
      if (i === 0) img.classList.add('is-visible');
      this.media.appendChild(img);
      return img;
    });
    this.shownIndex = 0;
  }

  assetUrl(name) {
    return `${this.dataset.assetPrefix || ''}${name}`;
  }

  preloadFrames(frames) {
    return Promise.all(
      frames.map(
        (frame) =>
          new Promise((resolve) => {
            const img = new Image();
            img.decoding = 'async';
            img.onload = () => resolve(frame);
            img.onerror = () => resolve(null);
            img.src = frame.url;
          })
      )
    ).then((list) => list.filter(Boolean));
  }

  async discoverMoreFrames() {
    const firstPng = this.assetUrl('zora-spin-01.png');
    if (!(await this.probe(firstPng))) return;

    const found = [];
    for (let i = 1; i <= 24; i += 1) {
      const url = this.assetUrl(`zora-spin-${String(i).padStart(2, '0')}.png`);
      if (!(await this.probe(url))) continue;
      found.push({ url, flip: false });
    }

    if (found.length > this.frames.length) {
      this.frames = found;
      this.count = found.length;
    }
  }

  probe(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  tick(ts) {
    if (!this.lastTs) this.lastTs = ts;
    const dt = Math.min(Math.max(ts - this.lastTs, 0), 32);
    this.lastTs = ts;

    if (this.count < 2) {
      this.raf = requestAnimationFrame(this.tick);
      return;
    }

    if (this.dragging) {
      /* drag updates in onMove */
    } else if (ts < this.resumeAt) {
      /* pause before auto resumes */
    } else if (Math.abs(this.velocity) > 0.0008) {
      this.position += this.velocity * (dt / 16.67);
      /* Longer, silkier coast */
      this.velocity *= Math.pow(0.978, dt / 16.67);
      if (Math.abs(this.velocity) < 0.0008) this.velocity = 0;
    } else if (this.auto && !this.reduced.matches) {
      this.position += (dt / this.cycleMs) * this.count;
    }

    this.wrapPosition();
    const index = this.frameIndex();
    if (index !== this.shownIndex) {
      this.showIndex(index);
    }

    this.raf = requestAnimationFrame(this.tick);
  }

  wrapPosition() {
    if (this.count <= 0) return;
    while (this.position < 0) this.position += this.count;
    while (this.position >= this.count) this.position -= this.count;
  }

  frameIndex() {
    return ((Math.round(this.position) % this.count) + this.count) % this.count;
  }

  showIndex(index) {
    const prev = this.imgs[this.shownIndex];
    const next = this.imgs[index];
    if (next) next.classList.add('is-visible');
    if (prev && prev !== next) prev.classList.remove('is-visible');
    this.shownIndex = index;
  }

  onWheel(e) {
    if (this.count < 2) return;
    e.preventDefault();
    const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
    this.velocity += delta * 0.0024;
    this.velocity = Math.max(-0.65, Math.min(0.65, this.velocity));
    this.resumeAt = performance.now() + 600;
  }

  onKey(e) {
    if (this.count < 2) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.velocity -= 0.12;
      this.resumeAt = performance.now() + 800;
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.velocity += 0.12;
      this.resumeAt = performance.now() + 800;
    }
  }

  onDown(e) {
    if (e.button > 0 || this.pointerId !== null) return;
    this.pointerId = e.pointerId;
    this.lastX = e.clientX;
    this.velocity = 0;
    this.moveSamples = [];
    this.dragging = true;
    this.classList.add('is-dragging');
    try {
      this.setPointerCapture(e.pointerId);
    } catch (_) {}
    window.addEventListener('pointermove', this.onMove, { passive: false });
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  onMove(e) {
    if (e.pointerId !== this.pointerId) return;
    e.preventDefault();
    const now = performance.now();
    const delta = e.clientX - this.lastX;
    this.lastX = e.clientX;

    /* ~0.85 widths = one full turn — light touch, premium feel */
    const track = Math.max(this.offsetWidth * 0.85, 260);
    const sensitivity = track / this.count;
    const step = delta / sensitivity;
    this.position += step;

    this.moveSamples.push({ t: now, d: step });
    if (this.moveSamples.length > 6) this.moveSamples.shift();

    this.wrapPosition();
    const index = this.frameIndex();
    if (index !== this.shownIndex) {
      this.showIndex(index);
    }
  }

  onUp(e) {
    if (e.pointerId !== this.pointerId) return;

    if (this.moveSamples.length >= 2) {
      const first = this.moveSamples[0];
      const last = this.moveSamples[this.moveSamples.length - 1];
      const dt = Math.max(last.t - first.t, 1);
      const total = this.moveSamples.reduce((sum, s) => sum + s.d, 0);
      this.velocity = (total / dt) * 16.67 * 1.15;
      this.velocity = Math.max(-0.75, Math.min(0.75, this.velocity));
    }

    this.release();
    this.dragging = false;
    this.classList.remove('is-dragging');
    this.resumeAt = performance.now() + (Math.abs(this.velocity) > 0.015 ? 120 : 900);
  }

  release() {
    this.pointerId = null;
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }
}

if (!customElements.get('zm-spin360')) {
  customElements.define('zm-spin360', ZmSpin360);
}
